// viewer.popups.js — pure HTML builders for pin/POI popups.
// Extracted from viewer.js so they're unit-testable without
// happy-dom mounting the full viewer (Leaflet + mp4box + canvas).

export function escapeHTML(s) {
  if (s == null) return "";
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export function safeColor(c, fallback = "#00ccff") {
  return /^#[0-9a-fA-F]{3,8}$/.test(c || "") ? c : fallback;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isLegacyOrphan(item) {
  const rid = item && item.createdBy && item.createdBy.reviewerId;
  return typeof rid === "string" && UUID_RE.test(rid);
}

export function isAdmin(reviewer) {
  if (!reviewer) return false;
  return reviewer.role === "admin";
}

export function isOwn(item, reviewer) {
  if (!reviewer || !reviewer.reviewerId) return true; // HDD shell — always editable
  if (isAdmin(reviewer)) return true;                 // admin reviewer — full edit/delete access
  if (!item.createdBy) return true;                   // legacy items pre-Plan 5
  if (isLegacyOrphan(item)) return true;              // legacy per-browser UUID — claimable
  return item.createdBy.reviewerId === reviewer.reviewerId;
}

function newNoteId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Returns a notes[] array where every entry has an `id`. Back-fills missing
// ids on legacy entries (deterministically using reviewerId+createdAt so the
// same row gets the same id across reloads of stale data; once persisted,
// the real UUID supersedes this and the back-fill never runs again).
export function migrateLegacyNote(item) {
  if (Array.isArray(item.notes) && item.notes.length > 0) {
    return item.notes.map((n) => n.id ? n : { ...n, id: legacyId(n) });
  }
  if (typeof item.note === "string" && item.note.length > 0 && item.createdBy) {
    return [{
      id: legacyId({
        reviewerId: item.createdBy.reviewerId,
        createdAt: item.createdAt,
      }),
      reviewerId: item.createdBy.reviewerId,
      displayName: item.createdBy.displayName,
      text: item.note,
      createdAt: item.createdAt,
      modifiedAt: item.modifiedAt,
    }];
  }
  return Array.isArray(item.notes) ? item.notes : [];
}

function legacyId(n) {
  return `legacy-${(n.reviewerId || "anon").slice(0, 12)}-${(n.createdAt || "0").replace(/[^0-9]/g, "").slice(0, 14) || "0"}`;
}

export function appendNote(notes, reviewer, rawText, now) {
  const text = String(rawText ?? "").trim();
  if (text.length === 0) return notes;
  const ts = now();
  return [...notes, {
    id: newNoteId(),
    reviewerId: reviewer.reviewerId,
    displayName: reviewer.displayName,
    text,
    createdAt: ts,
    modifiedAt: ts,
  }];
}

export function editNoteById(notes, id, rawText, now) {
  const text = String(rawText ?? "").trim();
  if (text.length === 0) return notes; // empty edit is a no-op; use removeNoteById to delete
  const ts = now();
  return notes.map((n) => n.id === id ? { ...n, text, modifiedAt: ts } : n);
}

export function removeNoteById(notes, id) {
  return notes.filter((n) => n.id !== id);
}

export function tooltipText(item, ctx) {
  const base = ctx.kind === "pin"
    ? item.name
    : `${item.name} — ${ctx.fmtVid(item.t)}`;
  if (!item.createdBy) return base;
  const reviewerId = ctx.reviewer ? ctx.reviewer.reviewerId : null;
  const label = reviewerId === null || reviewerId === item.createdBy.reviewerId
    ? "you"
    : item.createdBy.displayName;
  return `${base} · by ${label}`;
}

// ─── cardHTML: single info-card builder for pins and POIs ───────────────
//
// ctx shape:
//   {
//     kind: "pin" | "poi",
//     reviewer: ReviewerRef | null,
//     fmtVid: (seconds) => "MM:SS.S",
//     edit: boolean,                // pre-expand the edit panel?
//     hasFrame: boolean,            // POI-only: does a Frame.poiId === item.id exist?
//     linkedFrameId: string | null, // POI-only: id of the most recent linked frame
//   }
//
// Renders ALL UI states in one pass — edit panel and delete-confirm row are
// always in the DOM, hidden by CSS until toggled. This means UI state
// transitions never re-render the popup body, so the active reviewer's
// textarea is never wiped mid-edit.

const PRESET_COLORS = [
  "#ff4444", "#ff8800", "#ffcc00", "#44cc66",
  "#00ccff", "#3366ff", "#aa66ff", "#ffffff",
];

function relativeTime(iso) {
  if (!iso) return "";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const diffS = Math.max(0, (Date.now() - then) / 1000);
  if (diffS < 60) return "just now";
  if (diffS < 3600) return `${Math.floor(diffS / 60)} min ago`;
  if (diffS < 86400) return `${Math.floor(diffS / 3600)} h ago`;
  return `${Math.floor(diffS / 86400)} d ago`;
}

function attributionHTML(item, reviewer) {
  if (!item.createdBy) return "";
  const mine = !reviewer || reviewer.reviewerId === item.createdBy.reviewerId;
  const who = mine ? "you" : escapeHTML(item.createdBy.displayName);
  const rel = relativeTime(item.modifiedAt || item.createdAt);
  const tail = rel ? ` · ${rel}` : "";
  return `<div class="vmc-card-attr">by ${who}${tail}</div>`;
}

function swatchHTML(item) {
  const color = safeColor(item.color, "#ff4444");
  return `<span class="vmc-card-swatch" style="background:${color}"></span>`;
}

function editButtonHTML(item, reviewer) {
  const own = isOwn(item, reviewer);
  if (own) return `<button type="button" data-act="edit" class="vmc-card-link">Edit</button>`;
  const ownerName = escapeHTML(item.createdBy?.displayName ?? "another reviewer");
  return `<button type="button" data-act="edit" class="vmc-card-link" disabled title="Owned by ${ownerName}">Edit</button>`;
}

// Whether the active reviewer can delete other reviewers' note messages on
// this item. True when the reviewer is admin OR is the pin's creator.
export function canDeleteOthersNotes(item, reviewer) {
  if (!reviewer) return false;
  if (isAdmin(reviewer)) return true;
  return Boolean(item.createdBy && reviewer.reviewerId === item.createdBy.reviewerId);
}

// Renders one note message row in either "read" or "edit" mode.
export function noteRowHTML(n, opts) {
  const own = Boolean(opts.reviewer && n.reviewerId === opts.reviewer.reviewerId);
  const canEdit = own;
  const canDelete = own || Boolean(opts.canDeleteOthers);
  const ts = relativeTime(n.modifiedAt || n.createdAt);
  const editedTag = (n.modifiedAt && n.createdAt && n.modifiedAt !== n.createdAt) ? " (edited)" : "";
  const id = String(n.id ?? "");
  const idAttr = escapeHTML(id);
  if (opts.mode === "edit") {
    return `<div class="vmc-card-note-row vmc-editing-note" data-note-id="${idAttr}">
      <div class="vmc-card-note-meta">
        <span class="vmc-card-note-who">${escapeHTML(n.displayName)}${own ? " (you)" : ""}</span>
        <span class="vmc-card-note-time">${escapeHTML(ts)}${editedTag}</span>
      </div>
      <textarea class="vmc-card-note-edit" maxlength="2048" rows="2">${escapeHTML(n.text)}</textarea>
      <div class="vmc-card-note-edit-actions">
        <button type="button" data-act="note-edit-save" data-note-id="${idAttr}" class="vmc-card-link">Save</button>
        <button type="button" data-act="note-edit-cancel" data-note-id="${idAttr}" class="vmc-card-link">Cancel</button>
      </div>
    </div>`;
  }
  const editBtn = canEdit
    ? `<button type="button" data-act="note-edit" data-note-id="${idAttr}" class="vmc-card-link">Edit</button>` : "";
  const delBtn = canDelete
    ? `<button type="button" data-act="note-delete" data-note-id="${idAttr}" class="vmc-card-link vmc-card-link-danger">Delete</button>` : "";
  const actions = (editBtn || delBtn)
    ? `<span class="vmc-card-note-actions">${editBtn}${delBtn}</span>` : "";
  return `<div class="vmc-card-note-row" data-note-id="${idAttr}">
    <div class="vmc-card-note-meta">
      <span class="vmc-card-note-who">${escapeHTML(n.displayName)}${own ? " (you)" : ""}</span>
      <span class="vmc-card-note-time">${escapeHTML(ts)}${editedTag}</span>
      ${actions}
    </div>
    <div class="vmc-card-note-text">${escapeHTML(n.text)}</div>
  </div>`;
}

// Renders the inner HTML of `.vmc-card-notes-list` — chronologically-sorted
// chat messages. Re-callable by viewer.js to repaint after post/edit/delete
// without disturbing the compose textarea sibling.
export function notesListHTML(item, reviewer) {
  const notes = migrateLegacyNote(item);
  const sorted = [...notes].sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));
  const canDeleteOthers = canDeleteOthersNotes(item, reviewer);
  return sorted.map((n) => noteRowHTML(n, { reviewer, canDeleteOthers, mode: "read" })).join("");
}

function paletteHTML(currentColor) {
  const norm = (c) => (c || "").toLowerCase();
  const cur = norm(currentColor);
  const swatches = PRESET_COLORS.map((c) => {
    const selCls = norm(c) === cur ? " vmc-swatch-selected" : "";
    return `<button type="button" data-act="swatch" data-color="${c}" class="vmc-swatch${selCls}" style="background:${c}" aria-label="Set colour ${c}"></button>`;
  }).join("");
  const rainbow = `<button type="button" data-act="swatch-custom" class="vmc-swatch-custom" aria-label="Custom colour"></button>`;
  return `<div class="vmc-card-palette">${swatches}${rainbow}</div>`;
}

function frameRowHTML(item, ctx) {
  if (ctx.kind !== "poi") return "";
  if (ctx.hasFrame && ctx.linkedFrameId) {
    return `<div class="vmc-card-frame-row">
      <button type="button" data-act="view-frame" data-frame-id="${escapeHTML(ctx.linkedFrameId)}" class="vmc-card-link">View saved frame</button>
    </div>`;
  }
  const disabled = !item.createdBy ? " disabled" : "";
  const title = !item.createdBy ? ` title="Save the POI first by entering a name"` : "";
  return `<div class="vmc-card-frame-row">
    <button type="button" data-act="save-frame" class="vmc-card-link"${disabled}${title}>Save frame</button>
  </div>`;
}

function jumpRowHTML(item, ctx) {
  // POIs jump to their capture moment; pins have no timestamp, so the viewer
  // seeks to the nearest track point instead (wired in attachCardHandlers).
  if (ctx.kind === "poi" && typeof item.t !== "number") return "";
  return `<div class="vmc-card-frame-row vmc-card-jump-row">
    <button type="button" data-act="jump" class="vmc-card-link">Jump to</button>
  </div>`;
}

function metaHTML(item, ctx) {
  const ll = `${item.lat.toFixed(6)}, ${item.lon.toFixed(6)}`;
  if (ctx.kind === "poi") return `<div class="vmc-card-meta">Video ${ctx.fmtVid(item.t)} · ${ll}</div>`;
  return `<div class="vmc-card-meta">${ll}</div>`;
}

function thumbHTML(item, ctx) {
  if (ctx.kind !== "poi") return "";
  if (!item.thumb || !/^data:image\//.test(item.thumb)) return "";
  return `<img class="vmc-card-thumb" src="${escapeHTML(item.thumb)}" alt=""/>`;
}

export function cardHTML(item, ctx) {
  const bodyClasses = ["vmc-card-body"];
  if (ctx.edit) bodyClasses.push("vmc-editing");
  return `<div class="${bodyClasses.join(" ")}" data-kind="${ctx.kind}" data-tour="viewer.card">
    <div class="vmc-card-title-row">
      ${swatchHTML(item)}
      <span class="vmc-card-name">${escapeHTML(item.name)}</span>
      <span class="vmc-card-title-actions">
        ${editButtonHTML(item, ctx.reviewer)}
        <button type="button" data-act="close" class="vmc-card-close" aria-label="Close">×</button>
      </span>
    </div>
    ${attributionHTML(item, ctx.reviewer)}
    <div class="vmc-card-edit-panel">
      <label class="vmc-card-field">
        <span>Name</span>
        <input type="text" class="vmc-card-name-input" value="${escapeHTML(item.name)}" maxlength="120"/>
      </label>
      <label class="vmc-card-field">
        <span>Colour</span>
        ${paletteHTML(item.color)}
      </label>
      <input type="color" class="vmc-card-color-hidden" value="${safeColor(item.color, "#ff4444")}" tabindex="-1" aria-hidden="true"/>
      <div class="vmc-card-edit-actions">
        <button type="button" data-act="delete-ask" class="vmc-card-link vmc-card-link-danger">Delete…</button>
        <button type="button" data-act="done" class="vmc-card-link">Done</button>
      </div>
      <div class="vmc-card-delete-confirm hidden">
        <span>Delete ${escapeHTML(item.name)}?</span>
        <button type="button" data-act="delete-yes" class="vmc-card-link vmc-card-link-danger">Yes</button>
        <button type="button" data-act="delete-no" class="vmc-card-link">No</button>
      </div>
    </div>
    ${thumbHTML(item, ctx)}
    <div class="vmc-card-notes">
      <div class="vmc-card-notes-list">${notesListHTML(item, ctx.reviewer)}</div>
      ${ctx.reviewer ? `
        <div class="vmc-card-notes-compose">
          <textarea class="vmc-card-note-input" placeholder="Add a message…" maxlength="2048" rows="2"></textarea>
          <div class="vmc-card-notes-compose-actions">
            <button type="button" data-act="note-post" class="vmc-card-link">Send</button>
          </div>
        </div>` : ""}
    </div>
    ${frameRowHTML(item, ctx)}
    ${jumpRowHTML(item, ctx)}
    ${metaHTML(item, ctx)}
  </div>`;
}

/** The name a card's name field would commit: its trimmed value when that is
 *  non-empty and differs from the item's current name, else null. Shared by
 *  the blur commit and by every way a card can close (×, Escape, a click on
 *  the map), so a name typed and never blurred is not lost with the popup. */
export function pendingName(inputValue, currentName) {
  const next = (inputValue || "").trim();
  if (!next || next === currentName) return null;
  return next;
}

/** Whether a card close (or blur) should commit the name field, and to what.
 *  Only a field the reviewer actually typed in is committed: Leaflet rebuilds
 *  a re-opened card from the bound HTML, so an untouched field can hold a
 *  stale name that must never be written back. A deleted item, or one the
 *  reviewer does not own, never commits. Returns the new name or null. */
export function shouldCommitName({ dirty, deleted, own, inputValue, currentName }) {
  if (!dirty || deleted || !own) return null;
  return pendingName(inputValue, currentName);
}
